import * as React from 'react'

import { XYCoord, useDragLayer } from 'react-dnd'
import { Identifier } from 'dnd-core'

import { BOTTOM_PADDING } from '@/modules/drivers/tabs/activities/components_new/utils/layout'

import DelegationMarkerPreview from './DelegationMarkerPreview'
import {
  MARKER_VISUAL_HEIGHT,
  MARKER_VISUAL_WIDTH,
} from './new/DelegationMarkerVisual'
import { MARKER_DROP_SLACK, MarkerDropArea } from './new/markerDrag'

const layerStyles: React.CSSProperties = {
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 100,
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)


const isInsideDropArea = (
  offset: XYCoord,
  area?: MarkerDropArea | null,
): boolean =>
  !!area &&
  offset.x >= area.left &&
  offset.x <= area.left + area.width &&
  offset.y >= area.top - MARKER_DROP_SLACK &&
  offset.y <= area.top + area.height + MARKER_DROP_SLACK


const getPreviewLayout = (
  currentOffset?: XYCoord | null,
  area?: MarkerDropArea | null,
) => {
  if (!currentOffset) {
    return { style: { display: 'none' }, height: MARKER_VISUAL_HEIGHT }
  }

  const snapped = isInsideDropArea(currentOffset, area)
  const centerX = snapped
    ? clamp(currentOffset.x, area!.left, area!.left + area!.width)
    : currentOffset.x

  const x = centerX - MARKER_VISUAL_WIDTH / 2
  const y = snapped ? area!.top : currentOffset.y - MARKER_VISUAL_HEIGHT
  const transform = `translate(${x}px, ${y}px)`

  return {
    style: { transform, WebkitTransform: transform },
    height: snapped
      ? Math.max(0, area!.height - BOTTOM_PADDING)
      : MARKER_VISUAL_HEIGHT,
  }
}

export interface CustomDragLayerProps {
  item?: any
  itemType?: Identifier | null
  initialOffset?: XYCoord | null
  currentOffset?: XYCoord | null
  isDragging?: boolean
  snapToGrid?: boolean
}

const CustomDragLayer = (ownProps: CustomDragLayerProps) => {
  const collected = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    initialOffset: monitor.getInitialClientOffset(),
    currentOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
  }))
  const props: CustomDragLayerProps = { ...ownProps, ...collected }
  const { item, itemType, isDragging, currentOffset } = props

  if (!isDragging || itemType !== 'DELEGATION_MARKER') {
    return null
  }

  const layout = getPreviewLayout(currentOffset, item.dropArea)

  return (
    <div style={layerStyles}>
      <div style={layout.style}>
        <DelegationMarkerPreview
          marker={item}
          translate={item.translate}
          time={item.time}
          height={layout.height}
        />
      </div>
    </div>
  )
}

export default CustomDragLayer
